/**
 * Cuadro de conversación fijo «Despacho» (GEN-PX-004, spec §3.3).
 *
 * UNA sola pieza para escribir a un agente desde cualquier zona: la usan
 * ChatWindow (Inicio y conversación) y el armazón (resto de zonas). Pone la
 * mecánica del campo: Intro envía / Mayús+Intro salta de línea, crece hasta 6
 * líneas, «@» sugiere agentes (GEN-HU-033, lib/llamada.js) y adjuntar ficheros.
 * Qué hace el envío (hilo actual o abrir conversación) lo decide quien lo monta.
 *
 * Adjuntar: el kernel no tiene contrato de adjuntos por mensaje. En el MVP los
 * ficheros se suben a Inbox con el endpoint de siempre (uploadInboxFile) y el
 * mensaje lleva una nota con sus nombres. GEN-EKB-041: también carpetas enteras
 * (botón de carpeta, o arrastrándolas al cuadro), con su ruta relativa bajo Inbox:
 * la nota nombra la carpeta y Jimmy la aprende con una sola confirmación.
 * Lleva siempre la línea de aviso de IA (GEN-PX-005, AvisoIA): no se puede quitar.
 */
import { useState, useEffect, useRef, useId } from 'react'
import { ArrowUp, FolderUp, Loader, Paperclip, X } from 'lucide-react'
import { Avatar } from './ui.jsx'
import AvisoIA from './AvisoIA.jsx'
import AutoGrowTextarea from '../components/AutoGrowTextarea.jsx'
import { useFotoAgente } from '../context/FotosAgentes.jsx'
import { uploadInboxFile } from '../api/client.js'
import { AVISO_SABER } from '../lib/cuadro.js'
import { agenteLlamado, conLlamada, prefijoLlamada, sugerenciasLlamada } from '../lib/llamada.js'
import { agrupar, arrastraFicheros, itemsDeDrop, itemsDeLista, subirItem } from '../lib/subidaCarpeta.js'

const LINEA = 20            // px, = --dk-cuadro-linea
const MAX_LINEAS = 6
const MAX_ALTO = LINEA * MAX_LINEAS + 16  // + relleno vertical del campo (2 × --dk-s-2)

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`

/** Nota que acompaña al mensaje: ficheros sueltos por nombre y carpetas ({nombre: nº de ficheros}) con su recuento. */
export const notaAdjuntos = (nombres, carpetas = {}) => {
  let nota = nombres.length ? `\n\n[Ficheros adjuntos, subidos a Inbox: ${nombres.join(', ')}]` : ''
  const cs = Object.entries(carpetas)
  if (cs.length) nota += `\n\n[Carpetas adjuntas, subidas a Inbox: ${cs.map(([n, c]) => `${n} (${plural(c, 'fichero', 'ficheros')})`).join(', ')}]`
  return nota
}

export default function CuadroConversacion({
  agents = [],
  destinatario,            // { id?, nombre } al que va el mensaje si no se elige otro con @
  placeholder,
  etiqueta = 'Mensaje',    // aria-label del campo
  enConversacion = false,  // ancho máx. 900 px en vez de 1240
  deshabilitado = false,   // turno en curso / chat bloqueado
  mencionPedida,           // { nombre, n }: «+ Añadir agente» pide empezar por «@Nombre »
  onMencionConsumida,
  onEnviar,                // (texto) => void; el texto ya lleva la nota de adjuntos
  textoPedido,             // { texto, n }: escribe la frase en el campo SIN enviarla (sugerencias de Saber, GEN-EKB-022)
  onTextoConsumido,
  onNuevaConversacion,     // si viene, botón «Nueva conversación» junto a adjuntar / «KB ▾» (agente de la zona, 022c; GEN-BUG-007b en toda conversación)
  nuevaDeshabilitada = false,
  modoSaber = false,       // Saber: aviso con el nombre y su línea de permisos; «KB ▾» solo visual; adjuntar sin función aún
}) {
  const fotoDe = useFotoAgente()
  const [texto, setTexto] = useState('')
  const [ficheros, setFicheros] = useState([])   // items {file, ruta}: ruta relativa bajo Inbox («carpeta/sub/a.md»; un suelto, su nombre)
  const [arrastrando, setArrastrando] = useState(false)
  const [subiendo, setSubiendo] = useState(false)
  const [error, setError] = useState(null)
  const [idx, setIdx] = useState(0)
  const [cerradas, setCerradas] = useState(false)
  const campoRef = useRef(null)
  const dockRef = useRef(null)
  const idAviso = useId()
  const ficheroRef = useRef(null)
  const carpetaRef = useRef(null)
  const subidosRef = useRef({ sueltos: [], carpetas: {} })  // lo ya subido en intentos anteriores: la nota lo cuenta entero

  // El cuadro flota sobre el contenido: avisa de su alto al contenedor para que
  // las zonas dejen margen inferior y se pueda llegar al final.
  useEffect(() => {
    const el = dockRef.current
    const padre = el?.parentElement
    if (!el || !padre || typeof ResizeObserver === 'undefined') return undefined
    const medir = () => padre.style.setProperty('--dk-alto-cuadro', `${el.offsetHeight}px`)
    medir()
    const ro = new ResizeObserver(medir)
    ro.observe(el)
    return () => { ro.disconnect(); padre.style.removeProperty('--dk-alto-cuadro') }
  }, [])

  useEffect(() => {
    if (!mencionPedida) return
    setTexto(prev => conLlamada(prev, mencionPedida.nombre))
    setCerradas(false)
    campoRef.current?.focus()
    onMencionConsumida?.()
  }, [mencionPedida])  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!textoPedido) return
    setTexto(textoPedido.texto)
    setCerradas(true)
    campoRef.current?.focus()
    onTextoConsumido?.()
  }, [textoPedido])  // eslint-disable-line react-hooks/exhaustive-deps

  const sugerencias = cerradas ? [] : sugerenciasLlamada(agents, prefijoLlamada(texto))
  const hayLista = sugerencias.length > 0

  // Destinatario visible: el agente elegido con @ gana al del hilo/zona.
  const dest = agenteLlamado(texto, agents) ?? destinatario
  const destAgente = (dest && agents.find(a => (dest.id && a.id === dest.id) || a.nombre === dest.nombre)) ?? dest
  const nombreDest = dest?.nombre ?? destAgente?.nombre ?? ''

  // El aviso habla del agente del hilo/zona, no del que se elija con @ al escribir.
  const baseDest = agents.find(a => (destinatario?.id && a.id === destinatario.id) || a.nombre === destinatario?.nombre)
  const destinatarioFijo = { nombre: destinatario?.nombre ?? '', rol: baseDest?.rol ?? destinatario?.rol ?? '' }

  const avatarDe = (ag, tamano) => (
    <Avatar agente foto={fotoDe(ag)} iniciales={(ag?.nombre ?? '?').charAt(0).toUpperCase()} nombre={ag?.nombre ?? ''} tamano={tamano} />
  )

  function elegir(ag) {
    setTexto(conLlamada(texto, ag.nombre))
    setIdx(0)
    campoRef.current?.focus()
  }

  async function enviar() {
    const t = texto.trim()
    if (!t || deshabilitado || subiendo) return
    let nota = ''
    if (ficheros.length > 0) {
      setSubiendo(true)
      setError(null)
      const subidos = subidosRef.current
      for (const it of ficheros) {
        try {
          await subirItem(uploadInboxFile, 'inbox', it)
        } catch (err) {
          setSubiendo(false)
          setError(err?.code === 'forbidden'
            ? 'No tienes permiso para subir ficheros.'
            : `No se ha podido subir «${it.ruta}». Inténtalo de nuevo.`)
          return
        }
        const { carpetas } = agrupar([it])
        if (carpetas.length) subidos.carpetas[carpetas[0].nombre] = (subidos.carpetas[carpetas[0].nombre] ?? 0) + 1
        else subidos.sueltos.push(it.file.name)
        setFicheros(prev => prev.filter(x => x !== it))  // un reintento no repite los ya subidos
      }
      setSubiendo(false)
      nota = notaAdjuntos(subidos.sueltos, subidos.carpetas)
      subidosRef.current = { sueltos: [], carpetas: {} }
    }
    setTexto('')
    setError(null)
    onEnviar?.(t + nota)
  }

  function onKeyDown(e) {
    if (hayLista) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => (i + 1) % sugerencias.length); return }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setIdx(i => (i - 1 + sugerencias.length) % sugerencias.length); return }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        elegir(sugerencias[Math.min(idx, sugerencias.length - 1)])
        return
      }
      if (e.key === 'Escape') { e.preventDefault(); setCerradas(true); return }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      enviar()
    }
  }

  const anadir = (items) => { if (items.length) setFicheros(prev => [...prev, ...items]) }

  function onPick(e) {
    const nuevos = itemsDeLista(e.target.files)
    e.target.value = ''  // permite volver a elegir el mismo fichero o carpeta
    anadir(nuevos)
  }

  // Arrastrar ficheros o carpetas al cuadro (GEN-EKB-041): las carpetas conservan su estructura.
  const puedeSoltar = !deshabilitado && !subiendo
  function onDragOver(e) {
    if (!puedeSoltar || !arrastraFicheros(e.dataTransfer)) return
    e.preventDefault()
    setArrastrando(true)
  }
  async function onDrop(e) {
    if (!arrastraFicheros(e.dataTransfer)) return
    e.preventDefault()
    setArrastrando(false)
    if (!puedeSoltar) return
    try {
      anadir(await itemsDeDrop(e.dataTransfer))
    } catch {
      setError('No se ha podido leer lo que has soltado. Prueba con el botón de adjuntar.')
    }
  }

  // Lo adjuntado, por pastillas: un suelto por fichero y UNA por carpeta (con su recuento), que se quita entera.
  const { sueltos, carpetas } = agrupar(ficheros)
  const quitar = (items) => setFicheros(prev => prev.filter(x => !items.includes(x)))

  const puedeEnviar = !!texto.trim() && !deshabilitado && !subiendo

  return (
    <div className={'dk-cuadro' + (enConversacion ? ' dk-cuadro--conversacion' : '')} ref={dockRef}>
      <div className={'dk-cuadro__caja' + (arrastrando ? ' dk-cuadro__caja--arrastrando' : '')}
        onDragOver={onDragOver} onDragLeave={() => setArrastrando(false)} onDrop={onDrop}>
        {hayLista && (
          <ul className="dk-cuadro__menciones" role="listbox" aria-label="Agentes a los que llamar">
            {sugerencias.map((ag, i) => (
              <li
                key={ag.id}
                role="option"
                aria-selected={i === idx}
                className={'dk-cuadro__mencion' + (i === idx ? ' dk-cuadro__mencion--activa' : '')}
                onMouseDown={e => { e.preventDefault(); elegir(ag) }}
              >
                {avatarDe(ag, 22)}
                <span className="dk-cuadro__mencion-nombre">{ag.nombre}</span>
                {ag.rol && <span className="dk-cuadro__mencion-rol">{ag.rol}</span>}
              </li>
            ))}
          </ul>
        )}
        {ficheros.length > 0 && (
          <ul className="dk-cuadro__adjuntos" aria-label="Ficheros adjuntos">
            {sueltos.map((it, i) => (
              <li key={`${it.ruta}-${i}`} className="dk-cuadro__adjunto">
                <span className="dk-cuadro__adjunto-nombre">{it.file.name}</span>
                <button type="button" className="dk-cuadro__quitar" aria-label={`Quitar ${it.file.name}`}
                  onClick={() => quitar([it])} disabled={subiendo}>
                  <X size={12} strokeWidth={2} aria-hidden="true" />
                </button>
              </li>
            ))}
            {carpetas.map(c => (
              <li key={`carpeta-${c.nombre}`} className="dk-cuadro__adjunto dk-cuadro__adjunto--carpeta">
                <FolderUp size={12} strokeWidth={1.9} aria-hidden="true" />
                <span className="dk-cuadro__adjunto-nombre">{c.nombre}</span>
                <span className="dk-cuadro__adjunto-cuenta">{plural(c.items.length, 'fichero', 'ficheros')}</span>
                <button type="button" className="dk-cuadro__quitar" aria-label={`Quitar la carpeta ${c.nombre}`}
                  onClick={() => quitar(c.items)} disabled={subiendo}>
                  <X size={12} strokeWidth={2} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {error && <p className="dk-cuadro__error" role="alert">{error}</p>}
        <div className="dk-cuadro__fila">
          <span className="dk-cuadro__destinatario" title={nombreDest ? `Para ${nombreDest}` : undefined}>
            {avatarDe(destAgente ?? { nombre: nombreDest }, 32)}
          </span>
          <AutoGrowTextarea
            ref={campoRef}
            className="dk-cuadro__campo"
            maxHeight={MAX_ALTO}
            placeholder={placeholder}
            value={texto}
            onChange={e => { setTexto(e.target.value); setIdx(0); setCerradas(false) }}
            onKeyDown={onKeyDown}
            disabled={deshabilitado}
            aria-label={etiqueta}
            aria-describedby={idAviso}
          />
          <input ref={ficheroRef} type="file" multiple hidden onChange={onPick} data-testid="cuadro-fichero" />
          <input ref={carpetaRef} type="file" webkitdirectory="" hidden onChange={onPick} data-testid="cuadro-carpeta" />
          {onNuevaConversacion && (
            <button type="button" className="dk-cuadro__kb dk-cuadro__nueva" onClick={onNuevaConversacion} disabled={nuevaDeshabilitada}>
              Nueva conversación
            </button>
          )}
          {modoSaber && (
            <button type="button" className="dk-cuadro__kb" aria-label="Elegir KB" title="Elegir KB: disponible más adelante" disabled>
              KB ▾
            </button>
          )}
          <button type="button" className="dk-cuadro__btn" aria-label="Adjuntar ficheros" title="Adjuntar ficheros"
            onClick={() => ficheroRef.current?.click()} disabled={deshabilitado || subiendo}>
            <Paperclip size={18} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <button type="button" className="dk-cuadro__btn" aria-label="Adjuntar carpeta"
            title={modoSaber ? 'Adjuntar una carpeta entera: Jimmy la aprende con una sola confirmación' : 'Adjuntar una carpeta entera'}
            onClick={() => carpetaRef.current?.click()} disabled={deshabilitado || subiendo}>
            <FolderUp size={18} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <button type="button" className="dk-cuadro__enviar" aria-label="Enviar" onClick={enviar} disabled={!puedeEnviar}>
            {deshabilitado || subiendo
              ? <Loader className="icon-spin" size={18} strokeWidth={1.9} aria-hidden="true" />
              : <ArrowUp size={18} strokeWidth={1.9} aria-hidden="true" />}
          </button>
        </div>
        <AvisoIA
          id={idAviso}
          nombre={enConversacion || modoSaber ? destinatarioFijo.nombre : ''}
          complemento={modoSaber ? AVISO_SABER : ''}
          destinatario={destinatarioFijo.nombre}
          cargo={destinatarioFijo.rol}
        />
      </div>
    </div>
  )
}
